/**
 * useGroupCardActions
 * Group card actions: remove (leave/decline/cancel/delete), accept invitation.
 * Manages groupActionLoadingId to guard against double-click on Accept/Decline.
 */

import { useState, useCallback } from 'react'
import type { AxiosResponse } from 'axios'
import type { Group } from '../types/groups.types'
import { deleteGroup } from '../helpers/groupsApi'
import {
  leaveGroup,
  acceptGroupInvitation,
  declineGroupInvitation,
  cancelGroupInvitation,
} from '../helpers/groupMembersApi'

export interface UseGroupCardActionsProps {
  apiClient: <T = unknown>(endpoint: string, config?: { method: string }) => Promise<AxiosResponse<T>>
  queryClient: { invalidateQueries: (opts: { queryKey: string[] }) => Promise<unknown> }
  showDialog: (opts: {
    title: string
    message: string
    type: 'danger'
    confirmText: string
    cancelText: string
  }) => Promise<boolean>
  showAlert: (opts: {
    title: string
    message: string
    type: 'danger'
  }) => Promise<unknown>
  refreshGroups: () => Promise<void>
}

export interface UseGroupCardActionsReturn {
  handleRemoveGroup: (groupId: string, group?: Group) => Promise<void>
  handleAcceptGroupInvitation: (group: Group) => Promise<void>
  groupActionLoadingId: string | null
}

export function useGroupCardActions({
  apiClient,
  queryClient,
  showDialog,
  showAlert,
  refreshGroups,
}: UseGroupCardActionsProps): UseGroupCardActionsReturn {
  const [groupActionLoadingId, setGroupActionLoadingId] = useState<string | null>(null)

  const handleRemoveGroup = useCallback(
    async (groupId: string, group?: Group) => {
      const groupName = group?.name ?? 'this group'

      if (group?.invitationStatus === 'pending' && group?.invitationId) {
        const isReceived = group.invitationType === 'received'
        const confirmed = await showDialog({
          title: isReceived ? 'Decline invitation' : 'Cancel invitation',
          message: isReceived
            ? `Decline invitation to "${groupName}"?`
            : `Cancel invitation to "${groupName}"?`,
          type: 'danger',
          confirmText: isReceived ? 'Decline' : 'Cancel',
          cancelText: 'Keep',
        })
        if (!confirmed) return
        setGroupActionLoadingId(group.invitationId)
        try {
          if (isReceived) {
            await declineGroupInvitation(groupId, group.invitationId, apiClient)
          } else {
            await cancelGroupInvitation(groupId, group.invitationId, apiClient)
          }
          await queryClient.invalidateQueries({ queryKey: ['invitationCounts'] })
          await refreshGroups()
        } catch {
          await showAlert({
            title: 'Error',
            message: isReceived
              ? 'Could not decline the invitation.'
              : 'Could not cancel the invitation.',
            type: 'danger',
          })
        } finally {
          setGroupActionLoadingId(null)
        }
        return
      }

      const isLastMember = (group?.memberIds?.length ?? 0) <= 1
      const confirmed = await showDialog({
        title: isLastMember ? 'Delete group' : 'Leave group',
        message: isLastMember
          ? `You're the last member. Leave and delete "${groupName}"?`
          : `Leave "${groupName}"?`,
        type: 'danger',
        confirmText: isLastMember ? 'Delete' : 'Leave',
        cancelText: 'Cancel',
      })
      if (!confirmed) return
      try {
        if (isLastMember) {
          await deleteGroup(groupId, apiClient)
        } else {
          await leaveGroup(groupId, apiClient)
        }
        await refreshGroups()
      } catch {
        await showAlert({
          title: 'Error',
          message: isLastMember ? 'Could not delete the group.' : 'Could not leave the group.',
          type: 'danger',
        })
      }
    },
    [apiClient, refreshGroups, showDialog, showAlert]
  )

  const handleAcceptGroupInvitation = useCallback(
    async (group: Group) => {
      if (!group.invitationId) return
      setGroupActionLoadingId(group.invitationId)
      try {
        await acceptGroupInvitation(group.id, group.invitationId, apiClient)
        await queryClient.invalidateQueries({ queryKey: ['invitationCounts'] })
        await queryClient.invalidateQueries({ queryKey: ['groups'] })
        await queryClient.invalidateQueries({ queryKey: ['challenges'] })
        await queryClient.invalidateQueries({ queryKey: ['challenges', 'active'] })
        await queryClient.invalidateQueries({ queryKey: ['groups', group.id, 'challenges'] })
        await queryClient.invalidateQueries({ queryKey: ['groups', group.id, 'challenges', 'joinable'] })
        await queryClient.invalidateQueries({ queryKey: ['groups', 'dashboard', group.id] })
        await refreshGroups()
      } catch {
        await showAlert({
          title: 'Error',
          message: 'Could not accept the invitation. Try again.',
          type: 'danger',
        })
      } finally {
        setGroupActionLoadingId(null)
      }
    },
    [apiClient, queryClient, refreshGroups, showAlert]
  )

  return {
    handleRemoveGroup,
    handleAcceptGroupInvitation,
    groupActionLoadingId,
  }
}
